import { ApplicationRef, Injectable, NgZone } from '@angular/core';
import { NgbModal, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import {
  OfflineConnectivityModalComponent,
  OfflineModalVariant,
} from '@app/shared/modals/offline-connectivity-modal/offline-connectivity-modal.component';
import { NewRelicEventsService } from '@app/services/new-relic-events.service';
import { NEW_RELIC_EVENT } from '@app/enums/new-relic.enum';

/**
 * Priority levels — higher number = higher priority.
 * An active modal of higher priority cannot be dismissed by a lower-priority show().
 */
const VARIANT_PRIORITY: Record<OfflineModalVariant, number> = {
  network: 0,
  emergency: 1,
};

/**
 * Centralized service that owns the single fullscreen offline/emergency modal instance.
 *
 * Features:
 * - Single modal ref guarantee — prevents stacking.
 * - Priority: `emergency > network`. A network request cannot dismiss an active emergency modal.
 * - NgZone.run wrapper to ensure Angular change detection fires when called from outside
 *   the Angular zone (e.g. CUSS device callbacks running in native WebSocket handlers).
 * - Emergency variant adds a higher z-index class for guaranteed visibility.
 */
@Injectable({
  providedIn: 'root',
})
export class OfflineConnectivityModalService {
  private modalRef: NgbModalRef | null = null;
  private activeVariant: OfflineModalVariant | null = null;
  private emergencyOpenedAt: number | null = null;

  constructor(
    private readonly ngbModal: NgbModal,
    private readonly zone: NgZone,
    private readonly newRelicEvents: NewRelicEventsService,
    private readonly appRef: ApplicationRef
  ) {}

  /** The variant currently being displayed, or null if no modal is open. */
  get currentVariant(): OfflineModalVariant | null {
    return this.activeVariant;
  }

  /** Whether any modal is currently visible. */
  get isOpen(): boolean {
    return this.modalRef !== null;
  }

  /**
   * Shows the fullscreen modal for the given variant.
   * If a modal is already open with a higher-priority variant, the call is a no-op.
   * If a modal is open with equal or lower priority, it is replaced.
   */
  show(variant: OfflineModalVariant): void {
    this.zone.run(() => this._show(variant));
  }

  /**
   * Hides the modal only if the currently active variant matches `variant`.
   * Prevents a network-recovery from accidentally closing an emergency modal.
   */
  hide(variant: OfflineModalVariant): void {
    this.zone.run(() => this._hide(variant));
  }

  /** Force-closes whatever modal is open, regardless of variant. */
  forceClose(): void {
    this.zone.run(() => this._close());
  }

  // ─── Internal (runs inside NgZone) ──────────────────────────────────────────

  private _show(variant: OfflineModalVariant): void {
    // If something is already showing with higher priority, no-op
    if (this.activeVariant !== null) {
      const activePriority = VARIANT_PRIORITY[this.activeVariant];
      const requestedPriority = VARIANT_PRIORITY[variant];
      if (requestedPriority < activePriority) {
        return; // can't override emergency with network
      }
      if (requestedPriority >= activePriority && this.activeVariant !== variant) {
        // Higher or equal priority but different variant — close current and re-open
        this._close();
      } else if (this.activeVariant === variant) {
        // Same variant already showing — no-op
        return;
      }
    }

    const windowClass =
      variant === 'emergency'
        ? 'colibri-fullscreen-modal offline-connectivity-modal-window offline-connectivity-modal--emergency'
        : 'colibri-fullscreen-modal offline-connectivity-modal-window';

    this.modalRef = this.ngbModal.open(OfflineConnectivityModalComponent, {
      size: 'full',
      backdrop: 'static',
      keyboard: false,
      windowClass,
    });

    // Set the variant input on the component instance
    (this.modalRef.componentInstance as OfflineConnectivityModalComponent).variant = variant;

    this.activeVariant = variant;

    // Emit metric SYNCHRONOUSLY when emergency modal opens.
    // Cannot use trackEvent() because it wraps in setTimeout(0) which may never fire
    // if the SSBD kills this window before the next macrotask.
    if (variant === 'emergency') {
      this.emergencyOpenedAt = Date.now();
      try {
        const nr = (window as any).newrelic;
        if (nr?.addPageAction) {
          nr.addPageAction('KIOSK_SSBD', {
            subName: NEW_RELIC_EVENT.PRESSED_BUTTON_EMERGENCY,
            actionSubname: NEW_RELIC_EVENT.PRESSED_BUTTON_EMERGENCY,
            triggered_at: new Date().toISOString(),
            isSSBD: true,
          });
        }
      } catch {
        /* best-effort */
      }
    }

    // Clean up if modal is dismissed externally (shouldn't happen with static backdrop, but safety)
    this.modalRef.result.then(
      () => this._reset(),
      () => this._reset()
    );

    // Force change detection immediately — CUSS callbacks run outside Angular's zone
    // awareness so the DOM may not update until the next tick without this.
    this.appRef.tick();
  }

  private _hide(variant: OfflineModalVariant): void {
    if (this.activeVariant !== variant) {
      return; // Not ours to close
    }
    this._close();
  }

  private _close(): void {
    if (!this.modalRef) {
      return;
    }
    // Emit closed metric for emergency modal with duration
    if (this.activeVariant === 'emergency' && this.emergencyOpenedAt !== null) {
      const durationSeconds = Math.round((Date.now() - this.emergencyOpenedAt) / 1000);
      this.newRelicEvents.trackEvent(NEW_RELIC_EVENT.PRESSED_BUTTON_EMERGENCY, {
        triggered_at: new Date(this.emergencyOpenedAt).toISOString(),
        closed_at: new Date().toISOString(),
        duration_seconds: durationSeconds,
        status: 'closed',
      });
    }
    try {
      this.modalRef.dismiss();
    } catch {
      /* modal already closed */
    }
    this._reset();
  }

  private _reset(): void {
    this.modalRef = null;
    this.activeVariant = null;
    this.emergencyOpenedAt = null;
  }
}
